<template>
  <UIDialog
    :is-open
    :z-index
    title="Set Price"
    size="md"
    @close="handleClose"
  >
    <div class="assets-set-price__title">{{ assetName }}</div>
    <AssetPricesForm
      label="Apply"
      :default-date
      :disabled="loading"
      @submit="handleSubmit"
    />
  </UIDialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { AssetPriceForm, ModalClass } from '@types'

import { ASSET_FIELD, NOTIFICATION_DELAY } from '@/const'

import { handleCatchedError } from '@/helpers/common'

import { useNotifications } from '@/plugins/notification'

import useAnalyticsStore from '@/store/analytics'
import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useAssetPricesStore } from '@/store/assets/prices'

import AssetPricesForm from '@/entities/assets/components/AssetPricesForm.vue'
import { UIDialog } from '@ui/modals'

type Props = {
  ids: string[]
  modal: ModalClass<any>
}

type Emits = {
  complete: []
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const analyticsStore = useAnalyticsStore()
const assetsBunchStore = useAssetsBunchStore()
const assetsPricesStore = useAssetPricesStore()

const { progress, remove, update } = useNotifications()

const zIndex = computed(() => props.modal.zIndex)
const isOpen = computed(() => props.modal.isOpened)

const defaultDate = computed(() => analyticsStore.getDate)

const assetName = computed(() => {
  const asset = assetsBunchStore.getElementById(props.ids[0])
  return asset?.field(ASSET_FIELD.NAME).value
})

const loading = computed(() => assetsPricesStore.loadingAction)

const handleClose = () => {
  props.modal.close()
}

const handleSubmit = async (value: AssetPriceForm) => {
  const nid = await progress({
    message: 'Setting price to selected assets',
  })
  try {
    await Promise.all(
      props.ids.map(asset_id =>
        assetsPricesStore.store({
          ...value,
          asset_id,
        }),
      ),
    )
    await update(
      nid,
      {
        type: 'success',
        message: 'Price set',
      },
      NOTIFICATION_DELAY,
    )
    emit('complete')
    handleClose()
  } catch (e) {
    await remove(nid)
    handleCatchedError(e as string)
  }
}
</script>

<style>
.assets-set-price__title {
  @apply text-base sm:text-sm;
}
</style>
